import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/ui/logo';
import { Notice } from '@/components/ui/notice';
import { ProgressDots } from '@/components/ui/progress-dots';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { usePageTitle } from '@/hooks/use-page-title';
import { WelcomeStep } from './welcome-step';
import { StateStep } from './state-step';
import { ChildStep } from './child-step';
import { NextSteps } from './next-steps';

const TOTAL_STEPS = 4;

export function OnboardingFlow() {
  const { t } = useTranslation('onboarding');
  usePageTitle(t('flow.pageTitle'));
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { completeOnboarding } = useAuth();
  const stepLabels = [t('flow.stepWelcome'), t('flow.stepState'), t('flow.stepChild'), t('flow.stepNext')];

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));

  const handleFinish = async () => {
    setError('');
    const result = await completeOnboarding();
    if (result?.success === false) {
      setError(t('flow.completeFailed'));
      return;
    }
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-brand-slate-50 flex flex-col">
      {/* Header */}
      <div className="flex justify-center pt-8 pb-4">
        <Logo variant="light" size="md" />
      </div>

      {/* Progress */}
      <div className="flex flex-col items-center gap-1.5 pb-6">
        <ProgressDots
          current={step}
          total={TOTAL_STEPS}
          labels={stepLabels}
          testId="onboarding-progress"
        />
        <p className="text-xs text-brand-slate-500" aria-live="polite">
          {t('flow.stepOf', { current: step + 1, total: TOTAL_STEPS })}
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-4 pb-12">
        <div className="bg-white rounded-card border border-brand-slate-200 p-8 w-full max-w-xl" data-testid="onboarding-step">
          {error && <div className="mb-4"><Notice variant="error" title={error} /></div>}
          {step === 0 && <WelcomeStep onNext={next} />}
          {step === 1 && <StateStep onNext={next} onSkip={next} />}
          {step === 2 && <ChildStep onNext={next} onSkip={next} />}
          {step === 3 && <NextSteps onFinish={handleFinish} />}
        </div>
      </div>
    </div>
  );
}
